import { useState, useEffect } from 'react';
import { Box, Typography, Paper, CircularProgress, Alert } from '@mui/material';
import { useFormContext } from 'react-hook-form';
import { WizardFormData } from '../schemas';
import { IronDomeSummaryItem } from '../IronDomeSummaryTable';
import DetailRow from './DetailRow';
import IronDomeSummaryTable from '../IronDomeSummaryTable';
import useApi from '../../../hooks/useApi';
import { parseIronDomeCSV } from '../utils/parseIronDomeCSV';

interface CampaignSectionProps {
  entryDomains: any[];
  getDomainName: (domainId: string, domains: any[]) => string;
  getOfferGroupName: (groupId: string) => string;
  getCampaignName: (campaignId: string) => string;
  getFileName: (fileData: any) => string;
}

function CampaignSection({
  entryDomains,
  getDomainName,
  getOfferGroupName,
  getCampaignName,
  getFileName,
}: CampaignSectionProps) {
  const { watch } = useFormContext<WizardFormData>();
  const formData = watch();
  const { api: apiClient, createApiCall } = useApi();

  const [campaignSummary, setCampaignSummary] = useState<
    IronDomeSummaryItem[] | null
  >(null);
  const [campaignLoading, setCampaignLoading] = useState(false);
  const [campaignError, setCampaignError] = useState<string | null>(null);

  const fetchIronDomeSummary = createApiCall(
    async ({ signal }, csvData: any[]) => {
      const response = await apiClient.post(
        '/api/country/iron-dome/parseIronDome?summary=true',
        csvData,
        {
          signal,
          headers: {
            'Content-Type': 'application/json',
          },
        }
      );
      return response.data.summary as IronDomeSummaryItem[];
    }
  );

  const fetchCampaignSummary = async () => {
    const hasNoIronDomeFile =
      !formData.campaignIronDomeFile ||
      !(formData.campaignIronDomeFile instanceof File);

    if (hasNoIronDomeFile) {
      setCampaignSummary(null);
      return;
    }

    try {
      setCampaignLoading(true);
      setCampaignError(null);
      const parsedData = await parseIronDomeCSV(formData.campaignIronDomeFile);

      const csvHasData =
        parsedData && parsedData.csvData && parsedData.csvData.length > 0;

      if (!csvHasData) return;

      const summary = await fetchIronDomeSummary().call(parsedData.csvData);
      setCampaignSummary(summary);
    } catch (error: any) {
      console.error('Error fetching campaign Iron Dome summary:', error);
      setCampaignError('Failed to fetch summary');
      setCampaignSummary(null);
    } finally {
      setCampaignLoading(false);
    }
  };

  useEffect(() => {
    fetchCampaignSummary();
  }, []);

  return (
    <Paper sx={{ p: 3 }}>
      <Typography variant="h6" gutterBottom color="primary">
        Campaign
      </Typography>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        <DetailRow
          label="Auto Create"
          chipLabel={formData.autoCreateCampaign ? 'Yes' : 'No'}
          chipColor={formData.autoCreateCampaign ? 'success' : 'default'}
        />
        {formData.autoCreateCampaign ? (
          <>
            <DetailRow label="Name" value={formData.campaignName} />
            <DetailRow
              label="Entry Domain"
              value={getDomainName(
                formData.campaignEntryDomainId || '',
                entryDomains
              )}
            />
            <DetailRow
              label="Offer Group"
              value={getOfferGroupName(formData.offerGroupId || '')}
            />
            {formData.campaignIronDomeFile && (
              <>
                <DetailRow
                  label="Iron Dome File"
                  value={getFileName(formData.campaignIronDomeFile)}
                />
                {campaignLoading && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CircularProgress size={16} />
                    <Typography variant="body2" color="text.secondary">
                      Loading summary...
                    </Typography>
                  </Box>
                )}
                {campaignError && (
                  <Alert severity="error" sx={{ mt: 1 }}>
                    {campaignError}
                  </Alert>
                )}
                {campaignSummary && !campaignLoading && (
                  <Box sx={{ mt: 2 }}>
                    <Typography
                      variant="subtitle2"
                      sx={{ mb: 1, fontWeight: 'bold' }}
                    >
                      Iron Dome Summary:
                    </Typography>
                    <IronDomeSummaryTable summaryData={campaignSummary} />
                  </Box>
                )}
              </>
            )}
          </>
        ) : (
          <DetailRow
            label="Selected Campaign"
            value={getCampaignName(formData.selectedCampaignId || '')}
          />
        )}
      </Box>
    </Paper>
  );
}

export default CampaignSection;
